import { parsePalletCode, type PalletRef } from '@/lib/pallets';

/**
 * Сканер палетов в приложении (T-203). Текст QR на этикетке — ссылка `…/p/SWP-12-3`;
 * код можно и ввести руками или сканером-«пистолетом» (он печатает код и нажимает Enter).
 */
export function palletFromScan(raw: string): PalletRef | null {
  const text = raw.trim();
  const link = /\/p\/([^/?#\s]+)/.exec(text);
  let code = text;
  if (link?.[1]) {
    try {
      code = decodeURIComponent(link[1]);
    } catch {
      code = link[1];
    }
  }
  return parsePalletCode(code);
}

/** Путь, который открывает карточку партии на нужном палете (как ссылка из QR). */
export const palletPath = (p: PalletRef) => `/p/SWP-${String(p.batchId)}-${String(p.palletNo)}`;

type JsQR = typeof import('jsqr').default;
let jsqr: Promise<JsQR> | null = null;

/** jsQR — для браузеров без BarcodeDetector (Safari на iPhone); грузится, только когда нужен. */
function loadJsQR(): Promise<JsQR> {
  jsqr ??= import('jsqr').then((m) => m.default);
  return jsqr;
}

/** Найти QR в кадре RGBA; null — кода в кадре нет. */
export async function decodeRgba(
  data: Uint8ClampedArray,
  width: number,
  height: number,
): Promise<string | null> {
  const decode = await loadJsQR();
  // Этикетка — чёрным по белому: инверсию не пробуем, кадр разбирается вдвое быстрее.
  return decode(data, width, height, { inversionAttempts: 'dontInvert' })?.data ?? null;
}

interface DetectedBarcode {
  rawValue: string;
}
interface BarcodeDetectorLike {
  detect(source: CanvasImageSource): Promise<DetectedBarcode[]>;
}
interface BarcodeDetectorCtor {
  new (options: { formats: string[] }): BarcodeDetectorLike;
  getSupportedFormats?: () => Promise<string[]>;
}

export type FrameDecoder = (video: HTMLVideoElement) => Promise<string | null>;

// Кадр уменьшаем: QR этикетки крупный, а jsQR на полном кадре телефона медленный.
const MAX_SIDE = 640;

/** Распознаватель кадров: встроенный BarcodeDetector (Chrome на Android), иначе jsQR. */
export async function createDecoder(): Promise<FrameDecoder> {
  const Detector = (window as { BarcodeDetector?: BarcodeDetectorCtor }).BarcodeDetector;
  const formats = Detector?.getSupportedFormats
    ? await Detector.getSupportedFormats().catch(() => [])
    : [];
  if (Detector && formats.includes('qr_code')) {
    const detector = new Detector({ formats: ['qr_code'] });
    return async (video) => (await detector.detect(video))[0]?.rawValue ?? null;
  }
  const canvas = document.createElement('canvas');
  const ctx = canvas.getContext('2d', { willReadFrequently: true });
  return async (video) => {
    if (!ctx || !video.videoWidth || !video.videoHeight) return null;
    const scale = Math.min(1, MAX_SIDE / Math.max(video.videoWidth, video.videoHeight));
    canvas.width = Math.round(video.videoWidth * scale);
    canvas.height = Math.round(video.videoHeight * scale);
    ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
    const frame = ctx.getImageData(0, 0, canvas.width, canvas.height);
    return decodeRgba(frame.data, canvas.width, canvas.height);
  };
}
